import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import NotificationBell from '../NotificationBell';
import { breadcrumbFor } from './nav';

// Top bar (ADR-49): menu button on narrow screens, breadcrumb, notification
// bell, theme toggle and the signed-in account.

const iconButton =
  'flex h-10 w-10 items-center justify-center rounded-lg border border-line bg-surface text-body hover:bg-canvas ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus';

export const TopBar: React.FC<{ onOpenMenu: () => void }> = ({ onOpenMenu }) => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();
  const crumb = breadcrumbFor(pathname);

  return (
    <header className="flex min-h-[64px] items-center gap-3 border-b border-line bg-surface px-4 sm:px-6 lg:px-8">
      <button type="button" onClick={onOpenMenu} aria-label="Open menu" className={`${iconButton} md:hidden`}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
      </button>

      <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
        {crumb && (
          <ol className="flex items-center gap-1.5 text-sm text-muted">
            <li>{crumb[0]}</li>
            <li aria-hidden="true">/</li>
            <li aria-current="page" className="truncate font-medium text-body">{crumb[1]}</li>
          </ol>
        )}
      </nav>

      <div className="flex items-center gap-2">
        <NotificationBell />
        <button
          type="button"
          onClick={toggle}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className={iconButton}
        >
          {theme === 'dark' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>
          )}
        </button>
        <div className="ml-1 hidden items-center gap-2 sm:flex">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
          ) : (
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-xs font-bold text-sidebar" aria-hidden="true">
              {(user?.name || '?').slice(0, 1)}
            </span>
          )}
          <span className="max-w-[160px] truncate text-sm text-body">{user?.name}</span>
        </div>
        <button
          type="button"
          onClick={logout}
          className="min-h-[40px] rounded-lg px-3 text-sm text-muted hover:bg-canvas hover:text-body focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
        >
          Sign out
        </button>
      </div>
    </header>
  );
};
